import { useEffect, useState } from "react";
import { api, type NewsletterPreview } from "./api.js";

/**
 * Both off-cycle sends and editorial corrections require reviewing an immutable preview.
 *
 * Inline by default: a trigger button with the preview under it. A table that
 * wants the preview in its own full-width row (Schedules) renders this there
 * with `autoPrepare`, which builds the preview on mount and drops the trigger,
 * and `onClose`, called on Cancel and after queuing.
 */
export function NewsletterSend({ org, seriesId, correctionOf, onQueued, autoPrepare, onClose }: {
  org: string; seriesId: string; correctionOf?: string; onQueued?: () => void;
  autoPrepare?: boolean; onClose?: () => void;
}) {
  const [preview, setPreview] = useState<NewsletterPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testMsg, setTestMsg] = useState("");
  const prepare = async () => {
    setBusy(true); setError(""); setMessage(""); setTestMsg(""); setPreview(null);
    try { setPreview(await api.previewSeries(org, seriesId, correctionOf)); }
    catch (e) { setError(String(e)); }
    finally { setBusy(false); }
  };
  const send = async () => {
    if (!preview) return;
    setBusy(true); setError("");
    try {
      await api.sendSeriesNow(org, seriesId, preview.campaignId);
      setMessage(`Queued ${preview.campaignId}.`); setPreview(null); onQueued?.();
    } catch (e) { setError(String(e)); }
    finally { setBusy(false); }
  };
  const sendTest = async () => {
    if (!preview) return;
    const to = testTo.split(",").map((s) => s.trim()).filter(Boolean);
    if (!to.length) return;
    setTestBusy(true); setTestMsg("");
    try {
      const res = await api.sendTestEmail({
        orgId: org,
        listId: preview.listId,
        to,
        subject: preview.subject,
        template: { html: preview.html },
      });
      setTestMsg(`Queued "${res.subject}" to ${res.recipients.join(", ")}.`);
    } catch (e) {
      setTestMsg(String(e));
    } finally {
      setTestBusy(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (autoPrepare) void prepare(); }, [autoPrepare, org, seriesId, correctionOf]);
  const cancel = () => { setPreview(null); onClose?.(); };
  return <div>
    {(!autoPrepare || (!preview && !busy)) && <button className="btn ghost" disabled={busy} onClick={() => void prepare()}>{busy ? "Preparing…" : correctionOf ? "Preview corrected resend" : autoPrepare ? "Preview again" : "Preview send now"}</button>}
    {autoPrepare && busy && <p className="muted">Building the preview from the latest content…</p>}
    {error && <p className="err" role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    {preview && <div className="card" role="region" aria-label="Newsletter send preview">
      <h3>{preview.subject}</h3>
      <p>Audience: {preview.listId}{preview.segmentId ? ` · segment ${preview.segmentId}` : " · entire subscribed list"}. {preview.recovery ? `This retries edition ${preview.campaignId}.` : `This sends a new edition of ${seriesId}.`}</p>
      {preview.correctionOf && <p>Correction of {preview.correctionOf}. Readers who received the earlier edition can receive this correction.</p>}
      {preview.warnings.map((warning, i) => <p className="err" key={i}>{warning}</p>)}
      <p className="muted">Review the stories and ads below. Personal links are added per recipient. {preview.recovery ? "This recovers the pending edition without creating another send ID." : "The preview expires after 15 minutes."}</p>
      <iframe title="Newsletter content preview" sandbox="" srcDoc={preview.html} style={{width:"100%",height:450,border:"1px solid #ddd"}} />
      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "10px 0" }}>
        <input
          type="text"
          aria-label="Test email recipient"
          value={testTo}
          onChange={(e) => setTestTo(e.target.value)}
          placeholder="Send test copy to (e.g. editor@example.com)"
          style={{ flex: 1 }}
        />
        <button
          type="button"
          className="btn ghost"
          disabled={testBusy || testTo.trim() === ""}
          onClick={() => void sendTest()}
        >
          {testBusy ? "Sending test…" : "Send test email"}
        </button>
      </div>
      {testMsg && <p className={testMsg.startsWith("Queued") ? "muted" : "err"} role="status">{testMsg}</p>}
      {preview.canSend === false && <p className="muted">This series is paused. Resume it and refresh the preview before sending.</p>}
      <button className="btn" disabled={busy || preview.canSend === false} onClick={() => void send()}>{preview.recovery ? "Retry this reviewed pending edition" : "Send this reviewed edition to the audience"}</button>{" "}
      <button className="btn ghost" disabled={busy} onClick={cancel}>Cancel</button>
    </div>}
  </div>;
}
